import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send, ShieldCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LogoMark } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  "What documents do I need for a personal loan?",
  "How does your process work?",
  "Do you charge any fees?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const listRef = useRef(null);
  const [sid] = useState(() => {
    let s = localStorage.getItem("cn_chat_sid");
    if (!s) {
      s = crypto.randomUUID();
      localStorage.setItem("cn_chat_sid", s);
    }
    return s;
  });

  useEffect(() => {
    if (!open || historyLoaded) return;
    setHistoryLoaded(true);
    fetch(`${API}/chat/${sid}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch(() => {});
  }, [open, historyLoaded, sid]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, open]);

  const send = async (preset) => {
    const msg = (preset || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const fail = (text) =>
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: text };
        return c;
      });
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const data = JSON.parse(p.slice(5));
            if (data.t) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + data.t };
                return c;
              });
            }
            if (data.error) fail("Sorry, I could not answer right now. Please try again or submit an enquiry.");
          } catch {}
        }
      }
      setMessages((m) => {
        if (m[m.length - 1]?.content === "") {
          const c = [...m];
          c[c.length - 1] = { role: "assistant", content: "Sorry, I could not answer right now. Please try again or submit an enquiry." };
          return c;
        }
        return m;
      });
    } catch {
      fail("Network issue — please check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            key="fab"
            data-testid="chat-widget-button"
            aria-label="Open CREDNOVA AI assistant"
            onClick={() => setOpen(true)}
            initial={{ opacity: 0, scale: 0.6, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.6, y: 20 }}
            transition={{ delay: 0.2, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="btn-gold fixed bottom-[96px] left-5 lg:bottom-6 lg:left-6 z-[70] w-14 h-14 rounded-full flex items-center justify-center"
          >
            <span className="relative z-10">
              <MessageSquare size={22} strokeWidth={1.8} />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed left-3 right-3 bottom-[96px] sm:left-6 sm:right-auto sm:bottom-24 sm:w-[390px] h-[62vh] sm:h-[560px] z-[70] rounded-2xl border border-[rgba(212,175,55,0.35)] bg-[#0C0C10]/97 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[rgba(212,175,55,0.2)] bg-[#121216]">
              <LogoMark size={30} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white leading-tight">CREDNOVA Assistant</p>
                <p className="text-[10px] text-[#71717A]">AI-powered · Answers about our loan assistance services</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close-button"
                aria-label="Close chat"
                className="text-[#71717A] hover:text-white p-1 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-message-list">
              {messages.length === 0 && (
                <div className="pt-4">
                  <div className="flex items-start gap-2.5 mb-5">
                    <ShieldCheck size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
                    <p className="text-xs text-[#A1A1AA] leading-relaxed">
                      Hi! I can help with questions about loan types, eligibility, documents and our process.
                      I will never ask for OTPs, card or banking details, and loan approval is always the
                      institution's decision.
                    </p>
                  </div>
                  <div className="space-y-2">
                    {CHIPS.map((c) => (
                      <button
                        key={c}
                        data-testid={`chat-chip-${CHIPS.indexOf(c)}`}
                        onClick={() => send(c)}
                        className="block w-full text-left text-xs text-[#E6CA65] border border-[rgba(212,175,55,0.25)] rounded-xl px-3.5 py-2.5 hover:bg-[rgba(212,175,55,0.07)] transition-colors"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gradient-to-br from-[#BF953F] to-[#AA771C] text-[#0A0A0D] font-medium rounded-br-md"
                        : "bg-[#18181E] border border-[rgba(212,175,55,0.15)] text-[#E4E4E7] rounded-bl-md"
                    }`}
                  >
                    {m.content}
                    {m.role === "assistant" && m.content === "" && busy && (
                      <span className="inline-flex gap-1 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-bounce" style={{ animationDelay: "300ms" }} />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 pb-1.5">
              <p className="text-[9px] text-[#52525B] leading-snug">
                AI assistant — never share OTPs, card or banking details here. Loan approval is subject to the institution's criteria.
              </p>
            </div>
            <form
              className="flex items-center gap-2 px-3 pb-3 pt-1"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about loans, documents, process..."
                className="input-gold flex-1 !py-2.5 text-[13px]"
                maxLength={1000}
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="btn-gold w-10 h-10 rounded-full flex items-center justify-center shrink-0 disabled:opacity-50"
              >
                <span className="relative z-10">
                  <Send size={15} />
                </span>
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
